import { UserAvatar } from '@/components/ui/avatar';
import { cn, hashBucket, initials } from '@/lib/utils';

export type EntityKind = 'person' | 'company';
export type EntitySize = 'xs' | 'sm' | 'default' | 'lg';

/** Chart tokens are the only palette allowed to vary per record (ARCHITECTURE §3). */
const TONES = [
  'bg-chart-1/10 text-chart-1',
  'bg-chart-2/10 text-chart-2',
  'bg-chart-3/10 text-chart-3',
  'bg-chart-4/10 text-chart-4',
  'bg-chart-5/10 text-chart-5',
  'bg-chart-6/10 text-chart-6',
  'bg-chart-7/10 text-chart-7',
  'bg-chart-8/10 text-chart-8',
] as const;

const BOX: Record<EntitySize, string> = {
  xs: 'size-5 text-[10px] rounded',
  sm: 'size-6 text-[11px] rounded-sm',
  default: 'size-8 text-xs rounded-md',
  lg: 'size-10 text-sm rounded-md',
};

export interface EntityAvatarProps {
  name: string;
  kind?: EntityKind;
  src?: string;
  size?: EntitySize;
  className?: string;
  /**
   * El id de la ficha, para el color cuando la persona no dio su nombre.
   *
   * Sólo se pasa de largo hasta `UserAvatar`, que es donde se calcula el tono;
   * una empresa siempre tiene nombre y no lo necesita. Ver `semillaDeAvatar`.
   */
  seed?: string;
}

/**
 * People are round, companies are squared — the shape alone tells you which
 * kind of record a row is about before you read the name.
 */
export function EntityAvatar({
  name,
  kind = 'person',
  src,
  size = 'default',
  className,
  seed,
}: EntityAvatarProps) {
  if (kind === 'person') {
    return <UserAvatar name={name} src={src} size={size} className={className} seed={seed} />;
  }

  const tone = TONES[hashBucket(name, TONES.length)] ?? TONES[0];

  return (
    <span
      role="img"
      aria-label={name}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden border border-border/60 font-medium',
        BOX[size],
        !src && tone,
        className,
      )}
    >
      {src ? (
        <img src={src} alt="" className="size-full object-cover" loading="lazy" />
      ) : (
        initials(name)
      )}
    </span>
  );
}
